import React, { useEffect, useState, useCallback } from "react";
import { Compass, MapPin, CalendarDays, ExternalLink, X, Check, Plus, Ticket } from "lucide-react";
import { toast } from "sonner";
import { TabLayout } from "@/components/peeps/TabLayout";
import { HomeButton } from "@/components/peeps/HomeButton";
import { Spinner } from "@/components/peeps/ui";
import { api } from "@/lib/api";
import { colors } from "@/lib/theme";
import { longDate, formatTime } from "@/lib/date";

export default function Discover() {
  const [events, setEvents] = useState([]);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [configured, setConfigured] = useState(true);
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState("all");
  const [selected, setSelected] = useState(null);

  const load = useCallback(async (p, cat) => {
    if (p === 0) setLoading(true); else setLoadingMore(true);
    try {
      const res = await api.getDiscovery(p, 20, cat);
      setConfigured(res.configured);
      setCategories(res.categories || []);
      setHasMore(res.has_more);
      setEvents((prev) => (p === 0 ? res.events : [...prev, ...res.events]));
    } catch (e) {
      toast(e.message || "Couldn't load events");
    } finally {
      setLoading(false); setLoadingMore(false);
    }
  }, []);

  useEffect(() => { setPage(0); load(0, category); }, [category, load]);

  const loadMore = () => { const next = page + 1; setPage(next); load(next, category); };

  return (
    <TabLayout>
      <HomeButton />
      <div className="px-4 pt-4">
        <div className="flex items-center gap-2 mb-1">
          <Compass size={22} color={colors.brandSecondary} />
          <h1 className="font-display font-bold text-2xl" style={{ color: colors.onSurface }}>Discover</h1>
        </div>
        <p className="font-display text-[13.5px] mb-4" style={{ color: colors.onSurfaceTertiary }}>
          What's happening around Austin — tap any event to add it to a group's calendar.
        </p>

        {categories.length > 0 && (
          <div className="flex gap-2 mb-4 overflow-x-auto no-scrollbar">
            {["all", ...categories].map((c) => {
              const active = category === c;
              return (
                <button key={c} data-testid={`discover-cat-${c}`} onClick={() => setCategory(c)}
                  className="flex items-center rounded-full py-1.5 px-3.5 shrink-0 transition-all"
                  style={{ background: active ? colors.brand : colors.surfaceSecondary, border: `1px solid ${active ? colors.brand : colors.border}` }}>
                  <span className="font-display font-medium text-[12.5px]" style={{ color: active ? colors.onBrand : colors.onSurfaceTertiary }}>
                    {c === "all" ? "All" : c}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center pt-20"><Spinner /></div>
        ) : events.length === 0 ? (
          <div className="flex flex-col items-center pt-16 px-6 text-center gap-2">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mb-2" style={{ background: colors.brandTertiary }}>
              <Ticket size={28} color={colors.brandSecondary} />
            </div>
            <h2 className="font-display font-bold text-lg" style={{ color: colors.onSurface }}>No events yet</h2>
            <p className="font-display text-[13.5px]" style={{ color: colors.onSurfaceTertiary }}>
              {configured ? "The Austin feed is refreshing — check back shortly." : "Discover lights up once the events feed is connected."}
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-3" data-testid="discover-feed">
            {events.map((e) => (
              <button key={e.external_id} data-testid={`discover-card-${e.external_id}`} onClick={() => setSelected(e)}
                className="flex rounded-2xl overflow-hidden text-left transition-transform active:scale-[0.99]"
                style={{ background: colors.surfaceSecondary, border: `1px solid ${colors.border}` }}>
                <div className="w-24 shrink-0 relative" style={{ background: colors.surfaceTertiary }}>
                  {e.image
                    ? <img src={e.image} alt="" className="w-full h-full object-cover" style={{ minHeight: 108 }} />
                    : <div className="w-full h-full flex items-center justify-center" style={{ minHeight: 108 }}><Compass size={22} color={colors.muted} /></div>}
                  {e.is_major && (
                    <span className="absolute top-1.5 left-1.5 font-mono2 font-bold text-[8px] px-1.5 py-0.5 rounded-full" style={{ background: "#E5679E", color: "#1a1220" }}>MAJOR</span>
                  )}
                </div>
                <div className="flex-1 p-3 min-w-0">
                  <h3 className="font-display font-bold text-[14.5px] line-clamp-2" style={{ color: colors.onSurface }}>{e.title}</h3>
                  <div className="flex items-center gap-1 mt-1.5">
                    <CalendarDays size={12} color={colors.brandSecondary} />
                    <span className="font-display text-[12px]" style={{ color: colors.onSurfaceTertiary }}>
                      {e.date ? longDate(e.date) : "Date TBA"}{e.time ? ` · ${formatTime(e.time)}` : ""}
                    </span>
                  </div>
                  {e.more_dates > 0 && (
                    <span className="inline-block mt-0.5 font-display text-[10.5px]" style={{ color: colors.brandSecondary }}>+{e.more_dates} more date{e.more_dates === 1 ? "" : "s"}</span>
                  )}
                  {e.venue && (
                    <div className="flex items-center gap-1 mt-0.5">
                      <MapPin size={12} color={colors.onSurfaceTertiary} />
                      <span className="font-display text-[12px] truncate" style={{ color: colors.onSurfaceTertiary }}>{e.venue}</span>
                    </div>
                  )}
                  {e.category && (
                    <span className="inline-block mt-1.5 font-display text-[10.5px] px-2 py-0.5 rounded-full" style={{ background: colors.surfaceTertiary, color: colors.faint }}>{e.category}</span>
                  )}
                </div>
              </button>
            ))}

            {hasMore && (
              <button data-testid="discover-load-more" onClick={loadMore} disabled={loadingMore}
                className="flex items-center justify-center gap-2 rounded-xl py-3 mt-1"
                style={{ background: colors.surfaceSecondary, border: `1px solid ${colors.border}` }}>
                {loadingMore ? <Spinner /> : <span className="font-display font-bold text-[13px]" style={{ color: colors.onSurfaceTertiary }}>Load more</span>}
              </button>
            )}
          </div>
        )}
      </div>

      {selected && <DiscoverDetail event={selected} onClose={() => setSelected(null)} />}
    </TabLayout>
  );
}

function DiscoverDetail({ event, onClose }) {
  const [groups, setGroups] = useState([]);
  const [picked, setPicked] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [picker, setPicker] = useState(false);

  useEffect(() => {
    api.listGroups().then((g) => setGroups(g || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const add = async () => {
    if (!picked.length) { toast("Pick at least one group"); return; }
    setAdding(true);
    try {
      const res = await api.addToCalendar({
        external_id: event.external_id, title: event.title, date: event.date,
        time: event.time || "", location: event.location || event.venue || "",
        link: event.url || "", details: event.category || "",
        group_ids: picked,
      });
      toast(`Added to ${res.count} calendar${res.count === 1 ? "" : "s"}`);
      setPicker(false); onClose();
    } catch (e) { toast(e.message || "Couldn't add to calendar"); }
    finally { setAdding(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col" style={{ background: "rgba(0,0,0,0.6)" }} onClick={onClose}>
      <div className="mt-auto rounded-t-3xl max-h-[92vh] overflow-y-auto" style={{ background: colors.surface, border: `1px solid ${colors.border}` }} onClick={(e) => e.stopPropagation()} data-testid="discover-detail">
        {event.image && <img src={event.image} alt="" className="w-full object-cover rounded-t-3xl" style={{ maxHeight: 220 }} />}
        <div className="p-5">
          <div className="flex items-start justify-between gap-3">
            <h2 className="font-display font-bold text-xl leading-tight" style={{ color: colors.onSurface }}>{event.title}</h2>
            <button data-testid="discover-detail-close" onClick={onClose} className="shrink-0 p-1.5 rounded-full" style={{ background: colors.surfaceSecondary }}>
              <X size={16} color={colors.onSurfaceTertiary} />
            </button>
          </div>

          <div className="flex flex-col gap-2 mt-3">
            <div className="flex items-center gap-2">
              <CalendarDays size={15} color={colors.brandSecondary} />
              <span className="font-display text-[13.5px]" style={{ color: colors.onSurface }}>
                {event.date ? longDate(event.date) : "Date TBA"}{event.time ? ` · ${formatTime(event.time)}` : ""}
              </span>
            </div>
            {event.more_dates > 0 && (
              <span className="font-display text-[12px]" style={{ color: colors.brandSecondary }}>Runs {event.occurrences} dates · this adds the first ({longDate(event.date)})</span>
            )}
            {event.venue && (
              <div className="flex items-center gap-2">
                <MapPin size={15} color={colors.onSurfaceTertiary} />
                <span className="font-display text-[13.5px]" style={{ color: colors.onSurfaceTertiary }}>{event.location || event.venue}</span>
              </div>
            )}
            {event.category && (
              <span className="inline-block w-fit font-display text-[11.5px] px-2.5 py-1 rounded-full" style={{ background: colors.surfaceTertiary, color: colors.faint }}>
                {[event.category, event.genre].filter(Boolean).join(" · ")}
              </span>
            )}
            {event.url && (
              <a href={event.url} target="_blank" rel="noreferrer" data-testid="discover-source-link"
                className="flex items-center gap-1.5 mt-1 font-display font-medium text-[13px]" style={{ color: colors.brandSecondary }}>
                <ExternalLink size={14} /> View on Ticketmaster
              </a>
            )}
          </div>

          {!picker ? (
            <button data-testid="discover-add-open" onClick={() => setPicker(true)}
              className="flex items-center justify-center gap-2 w-full rounded-xl py-3.5 mt-5 transition-transform active:scale-[0.99]" style={{ background: colors.brand }}>
              <Plus size={17} color={colors.onBrand} />
              <span className="font-display font-bold text-[14px]" style={{ color: colors.onBrand }}>Add to calendar</span>
            </button>
          ) : (
            <div className="mt-5">
              <p className="font-display font-medium text-[11.5px] uppercase tracking-wide mb-2" style={{ color: colors.muted }}>
                Add to which groups?
              </p>
              {loading ? (
                <div className="flex justify-center py-6"><Spinner /></div>
              ) : groups.length === 0 ? (
                <p className="font-display text-[13px]" style={{ color: colors.onSurfaceTertiary }}>You're not in any groups yet.</p>
              ) : (
                <div className="flex flex-col gap-2">
                  {groups.map((g) => {
                    const on = picked.includes(g.id);
                    return (
                      <button key={g.id} data-testid={`discover-group-${g.id}`} onClick={() => toggle(g.id)}
                        className="flex items-center justify-between rounded-xl px-3.5 py-3 transition-all"
                        style={{ background: on ? colors.brand + "22" : colors.surfaceSecondary, border: `1px solid ${on ? colors.brand : colors.border}` }}>
                        <span className="font-display font-medium text-[14px]" style={{ color: colors.onSurface }}>{g.name}</span>
                        <span className="w-5 h-5 rounded-full flex items-center justify-center" style={{ background: on ? colors.brand : "transparent", border: `1.5px solid ${on ? colors.brand : colors.border}` }}>
                          {on && <Check size={13} color={colors.onBrand} />}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
              <button data-testid="discover-add-confirm" onClick={add} disabled={adding || !picked.length}
                className="flex items-center justify-center gap-2 w-full rounded-xl py-3.5 mt-3 transition-transform active:scale-[0.99]"
                style={{ background: picked.length ? colors.brand : colors.surfaceTertiary, opacity: picked.length ? 1 : 0.6 }}>
                {adding ? <Spinner style={{ borderTopColor: colors.onBrand }} /> : <Check size={17} color={colors.onBrand} />}
                <span className="font-display font-bold text-[14px]" style={{ color: colors.onBrand }}>
                  {adding ? "Adding…" : `Add to ${picked.length || ""} ${picked.length === 1 ? "group" : "groups"}`.trim()}
                </span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
